import { Component, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ActivatedRoute } from '@angular/router';

import { SMSNode } from '@modules/campaigns/Domain/Nodes/SMSNode';
import { SMSConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { SendMessageInspectorBase } from '../send-message-inspector.base';

@Component({
  selector: 'app-sms-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    DropdownModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './sms-inspector.component.html',
  styleUrls: ['./sms-inspector.component.scss'],
})
export class SmsInspectorComponent extends SendMessageInspectorBase<SMSNode, SMSConfig> {
  protected override get channelType() {
    return 'sms';
  }
  protected override get i18nPrefix() {
    return 'DATA.MODULES.INSPECTORS.SMS';
  }

  readonly selectedTemplateId = signal<string | null>(null);

  override get copyIds(): string[] {
    const id = this.selectedTemplateId();
    return id ? [id] : [];
  }

  protected override setCopyIds(ids: string[]) {
    this.selectedTemplateId.set(ids[0] ?? null);
  }

  protected override refreshCopyFromConfig(config: SMSConfig) {
    this.selectedTemplateId.set(config.copyIds?.[0] ?? null);
  }

  override get nodeValue(): SMSNode | null {
    const n = this.node();
    return n?.type === NodeType.SendSMS ? (n as SMSNode) : null;
  }

  protected override buildConfig(node: SMSNode, copyIds: string[]): SMSConfig {
    return {
      ...(node.config as SMSConfig),
      copyIds,
      copyId: copyIds[0] ?? null,
    } as SMSConfig;
  }

  protected override extractPreviewMessage(template: CopyModel | null): string {
    if (!template) return '';
    const block = template.blocks?.find(
      (b) => b.type === 'smsText' || b.type === 'text' || b.alias === 'smsText',
    );
    return block?.translations?.[0]?.text ?? '';
  }

  onTemplateChange(id: string) {
    this.selectedTemplateId.set(id);
    this.scheduleApplyChanges();
  }

  constructor(
    workflow: WorkflowStateService,
    translate: TranslateService,
    copiesService: CopiesService,
    route: ActivatedRoute,
  ) {
    super(workflow, translate, copiesService, route);
  }
}
